import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, Checkbox, FormControlLabel, InputAdornment, AccordionSummary, AccordionDetails } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker/DatePicker';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { GenderSelector } from '../../selectors/GenderSelector';
import { Client2PostalAddressPanel } from './Client2PostalAddressPanel';
import { Client2AddressPanel } from './Client2AddressPanel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { FieldSwitchButton } from '../components/FieldSwitchButton';
import { IndividualNameSummary, IndividualSelector } from '../../selectors/IndividualSelector';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Address, PlaceType } from '../../selectors/AddressSelector';
import { addEllipsis, arrayToString, arrayToStringWithSeparator } from '../../../helpers/commonHelper';
import { TitleAutocompleteSelector } from '../../selectors/TitleAutocompleteSelector';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('OtherDetails2Panel');

// Explicit request (EnquiryFormDrawer split): the "Client 2 Details" accordion (name, contact details, addresses).
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface OtherDetails2PanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    isClient2PostalAddressPanelExpanded: boolean;
    homeCountry: string;
    onDropDownSelect: (form: FormApi<Enquiry>, values: AnyObject, input: FieldInputProps<any, HTMLElement>, selection: any, name: string) => void;
    onGetPartyAddresses: (form: FormApi<Enquiry>, values: AnyObject, selection: IndividualNameSummary, name: string) => void;
    onSwitch: (form: FormApi<Enquiry>, values: AnyObject, name: string) => void;
    onContactDetailsChanged: (form: FormApi<any>, values: AnyObject) => void;
    birthDateValidator: FieldValidator<any>;
    getAddressLabelForPanel: (buildingName: string, level: string, streetNumber: string, street1: string, street2: string, suburb: string, addressState: string, postCode: string, country: string) => string;
    createAddressSelectionHandler: (form: FormApi<Enquiry>, values: AnyObject) => (placeType: PlaceType | null, address: Address, name: string) => void;
    createInputChangedHandler: (form: FormApi<any>, name: string | any) => (value: string) => void;
    isSecondClientPoBoxRequired: (values: AnyObject) => boolean;
    requiredValidator: FieldValidator<any>;
    onClient2PostalAddressPanelExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    isSourceTypeClientOnly: true | undefined;
}

interface OtherDetails2PanelBodyProps extends OtherDetails2PanelProps {
    formValues: AnyObject;
}

const OtherDetails2PanelBody: React.FC<OtherDetails2PanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isExpanded, isClient2PostalAddressPanelExpanded, homeCountry, onDropDownSelect, onGetPartyAddresses, onSwitch, onContactDetailsChanged, birthDateValidator, getAddressLabelForPanel, createAddressSelectionHandler, createInputChangedHandler, isSecondClientPoBoxRequired, requiredValidator, onClient2PostalAddressPanelExpandChange, onExpandChange, isSourceTypeClientOnly,
        formValues,
    } = bodyProps;
    const values = formValues;

    const panelTitle = addEllipsis(arrayToString([
        arrayToStringWithSeparator([
            values.secondClientTitle && values.secondClientTitle.trim().length > 0 && values.secondClientTitle !== 'None' ? `${values.secondClientTitle}` : '',
            values.secondClientFirstName,
            values.secondClientLastName,
        ], ' '),
        values.secondClientRelationshipToEnquirer,
        values.secondClientAddressName,
        arrayToStringWithSeparator([
            arrayToStringWithSeparator([
                values.secondClientFloor,
                values.secondClientNumber,
            ], '/'),
            values.secondClientStreet1,
        ], ' '),
        values.secondClientStreet2,
        values.secondClientSuburb,
        values.secondClientState,
        values.secondClientPostCode,
        values.secondClientCountry
        // tslint:disable-next-line: align
    ]), 70);

    return (
        <Accordion
            className={classes.expansionPanel}
            expanded={isExpanded}
            onChange={onExpandChange}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="other-details2-content"
                id="other-details2-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipBlue)}>
                        <PersonAddIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelBlue, classes.sectionLabelText)}>
                        Client 2 Details
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        {panelTitle}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientTitle"
                            // validate={required} 
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <>
                                    <TitleAutocompleteSelector
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Title"
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        value={input.value ? { label: input.value, value: input.value } : undefined}
                                        // tslint:disable-next-line: max-line-length
                                        onSelection={(selection: IAutoCompleteItem, name: string) => {
                                            input.onChange(selection ? selection.value : '');
                                        }}
                                        // required={true}
                                        helperText={meta.error && meta.touched ? 'Title is required' : ''}
                                    />
                                </>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientFirstName"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Client 2 Given Name(s)"
                                    multiline={true}
                                    disabled={values.secondClientGuid}
                                    // required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Client 2 Given Name(s) is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div className={classes.toggleFieldWrapepr}>
                            <div className="field">
                                {
                                    values.isSecondClientNameSwitched ? (
                                        <Field
                                            name="secondClientIndividualSelector"
                                            subscription={{ touched: true, error: true, value: true }}
                                        >
                                            {({ input, meta }) => (
                                                <IndividualSelector
                                                    variant="outlined"
                                                    size="small"
                                                    {...input}
                                                    key="clientIndividualSelector"
                                                    label="Individual Name"
                                                    value={values.secondClientIndividualSelector}
                                                    error={meta.error && meta.touched}
                                                    helperText={
                                                        meta.error &&
                                                            meta.touched
                                                            ? 'Individual Name is required'
                                                            : ''
                                                    }
                                                    onSelection={(selection: IndividualNameSummary, name: string) => {
                                                        const values = form.getState().values;
                                                        input.onChange(selection);
                                                        onDropDownSelect(form, values, input, selection, 'secondClientIndividualSelector');
                                                        onGetPartyAddresses(form, values, selection, 'secondClientIndividualSelector');
                                                    }}
                                                    enableSecondary={true}
                                                    includeOrganisation={false}
                                                    isSourceTypeClientOnly={isSourceTypeClientOnly}
                                                    endAdornmentExtra={(
                                                        <FieldSwitchButton
                                                            tooltip="Switch to enter the last name"
                                                            onClick={() => onSwitch(form, form.getState().values, 'secondClientLastName')}
                                                        />
                                                    )}
                                                // value={input.value}
                                                />
                                            )}
                                        </Field>
                                    ) :
                                        (
                                            <Field
                                                name="secondClientLastName"
                                                // validate={!isTabActivity() ? required : undefined}
                                                subscription={{ touched: true, error: true, value: true }}
                                            >
                                                {({ input, meta }) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        {...input}
                                                        label="Client 2 Surname"
                                                        type="text"
                                                        // required={!isTabActivity()}
                                                        className={classes.textField}
                                                        error={
                                                            meta.error &&
                                                            meta.touched
                                                        }
                                                        disabled={values.secondClientGuid}
                                                        autoComplete="abcd"
                                                        helperText={
                                                            meta.error &&
                                                                meta.touched
                                                                ? 'Client 2 Surname is required'
                                                                : ''
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <FieldSwitchButton
                                                                        tooltip="Switch to select an existing client & contact"
                                                                        onClick={() => onSwitch(form, form.getState().values, 'secondClientLastName')}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        )
                                }
                            </div>
                        </div>
                    </Grid>

                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientRelationshipToEnquirer"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Relationship to the Client"
                                    multiline={true}
                                    // required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Relationship to Client is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientEmail"
                            type="email"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Email"
                                        type="email"
                                        // required={!isTabActivity() ? getCorrespondenceRequired(values) : undefined}
                                        className={classes.textField}
                                        // tslint:disable-next-line: no-any
                                        onChange={(event: any) => {
                                            const values = form.getState().values;
                                            input.onChange(event.target.value);
                                            onContactDetailsChanged(form, values);
                                        }}
                                        error={
                                            meta.error &&
                                            meta.touched
                                        }
                                        autoComplete="abcd"
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? meta.error
                                                : ''
                                        }
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientPhone"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Telephone"
                                    type="text"
                                    // tslint:disable-next-line: no-any
                                    onChange={(event: any) => {
                                        const values = form.getState().values;
                                        input.onChange(event.target.value);
                                        onContactDetailsChanged(form, values);
                                    }}
                                    // required={true}
                                    // required={!isTabActivity() ? getCorrespondenceRequired(values) : undefined}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? meta.error // 'Phone is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientMobile"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Mobile"
                                    type="text"
                                    // tslint:disable-next-line: no-any
                                    onChange={(event: any) => {
                                        const values = form.getState().values;
                                        input.onChange(event.target.value);
                                        onContactDetailsChanged(form, values);
                                    }}
                                    // required={true}
                                    // required={!isTabActivity() ? getCorrespondenceRequired(values) : undefined}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? meta.error // 'Mobile is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientDOB"
                            validate={birthDateValidator}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <KeyboardDatePicker
                                    inputVariant="outlined"
                                    size="small"
                                    {...input}
                                    value={input && input.value ? input.value : null}
                                    className={classes.textField}
                                    label="Birthdate (of Client)"
                                    format="DD/MM/YYYY"
                                    placeholder="dd/mm/yyyy"
                                    animateYearScrolling={true}
                                    // tslint:disable-next-line: max-line-length
                                    autoOk={true}
                                    allowKeyboardControl={false}
                                    variant={'inline'}
                                    error={meta.error && meta.touched}
                                    helperText={meta.error}
                                    inputProps={{ autoComplete: 'off' }}
                                    InputLabelProps={{ shrink: true }}
                                    maxDate={new Date()}
                                    onChange={(date: MaterialUiPickersDate | null, value?: string | null) => input.onChange(date)}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            // tslint:disable-next-line: max-line-length
                            name="secondClientGender"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <GenderSelector
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Gender (of Client)"
                                        name="secondClientGender"
                                        disabled={values.secondClientGuid}
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        // tslint:disable-next-line: max-line-length
                                        onSelection={(selection: IAutoCompleteItem, name: string) =>
                                            input.onChange(selection.value)
                                        }
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? 'Gender is required'
                                                : ''
                                        }
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Grid container={true} spacing={1}>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="secondClientIsMarketingConsent"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.secondClientIsMarketingConsent}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            disabled={!!values.secondClientIndividualSelector}
                                            label="Marketing Consent"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="secondClientIsTermsAndConditionsApproved"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.secondClientIsTermsAndConditionsApproved}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="T&C's Approved"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="secondClientIsPrivacyStatementProvided"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.secondClientIsPrivacyStatementProvided}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="Privacy Statement Provided"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="secondClientIsConflictCheckPerformed"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.secondClientIsConflictCheckPerformed}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="Conflict Check Performed"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="secondClientIsNoConflict"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.secondClientIsNoConflict}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="No Conflict"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="secondClientIsIdentityCheckPerformed"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.secondClientIsIdentityCheckPerformed}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="Identity Check Peformed"
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item={true} xs={12}>
                        <Client2AddressPanel
                            form={form}
                            classes={classes}
                            defaultExpanded={true}
                            homeCountry={homeCountry}
                            getAddressLabelForPanel={getAddressLabelForPanel}
                            createAddressSelectionHandler={createAddressSelectionHandler}
                            createInputChangedHandler={createInputChangedHandler}
                        />
                    </Grid>
                    <Grid item={true} xs={12}>
                        <Client2PostalAddressPanel
                            form={form}
                            classes={classes}
                            isExpanded={isClient2PostalAddressPanelExpanded}
                            homeCountry={homeCountry}
                            isSecondClientPoBoxRequired={isSecondClientPoBoxRequired}
                            requiredValidator={requiredValidator}
                            createAddressSelectionHandler={createAddressSelectionHandler}
                            createInputChangedHandler={createInputChangedHandler}
                            onExpandChange={onClient2PostalAddressPanelExpandChange}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
OtherDetails2PanelBody.displayName = 'OtherDetails2PanelBody';

export const OtherDetails2Panel: React.FC<OtherDetails2PanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <OtherDetails2PanelBody
                {...panelProps}
                formValues={{
                    secondClientTitle: values.secondClientTitle,
                    secondClientFirstName: values.secondClientFirstName,
                    secondClientLastName: values.secondClientLastName,
                    secondClientRelationshipToEnquirer: values.secondClientRelationshipToEnquirer,
                    secondClientAddressName: values.secondClientAddressName,
                    secondClientFloor: values.secondClientFloor,
                    secondClientNumber: values.secondClientNumber,
                    secondClientStreet1: values.secondClientStreet1,
                    secondClientStreet2: values.secondClientStreet2,
                    secondClientSuburb: values.secondClientSuburb,
                    secondClientState: values.secondClientState,
                    secondClientPostCode: values.secondClientPostCode,
                    secondClientCountry: values.secondClientCountry,
                    secondClientGuid: values.secondClientGuid,
                    isSecondClientNameSwitched: values.isSecondClientNameSwitched,
                    secondClientIndividualSelector: values.secondClientIndividualSelector,
                    secondClientIsMarketingConsent: values.secondClientIsMarketingConsent,
                    secondClientIsTermsAndConditionsApproved: values.secondClientIsTermsAndConditionsApproved,
                    secondClientIsPrivacyStatementProvided: values.secondClientIsPrivacyStatementProvided,
                    secondClientIsConflictCheckPerformed: values.secondClientIsConflictCheckPerformed,
                    secondClientIsNoConflict: values.secondClientIsNoConflict,
                    secondClientIsIdentityCheckPerformed: values.secondClientIsIdentityCheckPerformed,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
OtherDetails2Panel.displayName = 'OtherDetails2Panel';
